import { useState } from "react";
import { ArrowRight, CheckCircle2, Clock, FileSignature, Loader2, X } from "lucide-react";
import { errorMessage } from "../../hooks/useCommission";
import { fmtDate, fmtMoney } from "../proposal/format";
import { Notice } from "./fields";

const PILL = "no-print inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-mono font-semibold tracking-[0.14em] uppercase transition-all whitespace-nowrap";
// Hero variant: sits on the navy band under the proposal meta, sized as the page's primary call to action.
const HERO = "no-print inline-flex items-center gap-2.5 rounded-md px-5 py-3 text-[15px] font-semibold transition-all whitespace-nowrap";

const CONTINUE = {
  commissioned: "Continue commissioning",
  medicys_sow: "Continue commissioning",
  sign_failed: "Sign again",
  client_paper: "Continue commissioning",
  client_paper_failed: "Send details again",
};

const statusCls = (active, tone, variant = "pill") => {
  if (variant === "hero") {
    const tones = {
      emerald: active ? "border-emerald-300 bg-emerald-400 text-[#0B1220]" : "border-emerald-400/50 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25",
      sky: active ? "border-[#9FD8F2] bg-[#3EA8DC] text-[#0B1220]" : "border-[#3EA8DC]/50 bg-[#3EA8DC]/15 text-[#BFE6F7] hover:bg-[#3EA8DC]/25",
      amber: active ? "border-amber-300 bg-amber-400 text-[#0B1220]" : "border-amber-400/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25",
    };
    return `${HERO} border ${tones[tone]}`;
  }
  const tones = {
    emerald: active ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
    sky: active ? "border-[#1B4F8A] bg-[#1B4F8A] text-white" : "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100",
    amber: active ? "border-amber-700 bg-amber-700 text-white" : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100",
  };
  return `${PILL} border ${tones[tone]}`;
};

function ConfirmDialog({ state, d, onClose, onConfirm, busy, error }) {
  const company = state.client_company || d.client_company;
  const net = state.totals?.net_total ?? d.sub_total;
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 whitespace-normal text-left"
      role="dialog"
      aria-modal="true"
      data-testid="commission-confirm-dialog"
      onClick={busy ? undefined : onClose}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          data-testid="commission-confirm-close"
          className="absolute top-3 right-3 text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-40"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="px-6 pt-6 pb-5">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#1B4F8A]">Commission this project</p>
          <h2 className="font-serif font-light text-2xl sm:text-3xl text-stone-900 leading-tight mt-2">Ready to go ahead?</h2>
          <p className="text-[15px] text-stone-600 leading-relaxed mt-3">
            Confirming lets the Medicys team reserve the people and resources for this project. You then choose how the paperwork is handled, either by signing our Statement of Work online or by using your own contract. Nothing is final until the contract is in place.
          </p>
        </div>
        <dl className="grid grid-cols-3 gap-4 bg-stone-50 border-y border-stone-200 px-6 py-4">
          <div className="col-span-3">
            <dt className="font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500">Proposal #{d.proposal_number}</dt>
            <dd className="font-serif text-lg text-stone-900 leading-snug mt-0.5" data-testid="commission-confirm-ref">{d.project_reference}</dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500">Net value</dt>
            <dd className="font-mono text-[15px] text-stone-900 mt-0.5" data-testid="commission-confirm-value">{fmtMoney(net, "£", 2)}</dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500">Valid until</dt>
            <dd className={`text-[15px] mt-0.5 ${state.expired ? "text-amber-700" : "text-stone-900"}`} data-testid="commission-confirm-validity">{fmtDate(state.valid_until)}{state.expired ? " (expired)" : ""}</dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500">For</dt>
            <dd className="text-[15px] text-stone-900 mt-0.5 truncate">{company}</dd>
          </div>
        </dl>
        <div className="px-6 py-5 flex flex-col gap-4">
          {state.expired && (
            <Notice tone="warning" title="This quote is 90 days or older" testid="commission-expired-note">
              It cannot be commissioned until Medicys has confirmed that the costs still stand. When you press the button below, an email is sent to Medicys asking for that confirmation. Nothing is agreed and nothing else happens until {state.bd_name || "your Medicys contact"} comes back to you.
            </Notice>
          )}
          {state.costs_refreshed && (
            <Notice tone="success" title="Costs confirmed by Medicys" testid="commission-costs-refreshed-note">
              Medicys has reconfirmed the costs in this proposal. The quote is valid until {fmtDate(state.valid_until)} and you can commission the project.
            </Notice>
          )}
          {error && <Notice tone="danger" testid="commission-error">{error}</Notice>}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button type="button" onClick={onClose} disabled={busy} data-testid="commission-confirm-cancel" className="text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors disabled:opacity-40 text-left">
              Not yet
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid="commission-start"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1B4F8A] hover:bg-[#143E72] text-white font-semibold px-5 py-2.5 text-[15px] transition-colors disabled:opacity-50"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> One moment</>) : (<>{state.expired ? "Ask Medicys to confirm the costs" : "Yes, commission this project"} <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </div>
          <p className="text-xs text-stone-500 leading-relaxed">By continuing you confirm that you are authorised to commission work on behalf of {company}.</p>
        </div>
      </div>
    </div>
  );
}

// The commissioning call to action. In the hero (variant "hero") it is
// the page's primary button; as a slim pill it can sit in a toolbar. It
// doubles as the tab for the commissioning panel: the first click asks
// for confirmation, later clicks open the panel.
export function CommissionButton({ commission, d, onOpenTab, active = false, variant = "pill" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const state = commission.state;
  if (!state || state.closed) return null;
  const { stage } = state;
  const hero = variant === "hero";
  const icon = hero ? "w-4 h-4" : "w-3.5 h-3.5";

  if (stage === "signed" || stage === "signed_elsewhere" || stage === "client_signed" || stage === "countersigning" || stage === "countersign_failed") {
    return (
      <button type="button" onClick={onOpenTab} aria-pressed={active} className={statusCls(active, "emerald", variant)} data-testid="commission-cta-signed">
        <CheckCircle2 className={icon} /> Commissioned
      </button>
    );
  }
  if (stage === "client_paper_submitted") {
    return (
      <button type="button" onClick={onOpenTab} aria-pressed={active} className={statusCls(active, "sky", variant)} data-testid="commission-cta-client-paper-done">
        <CheckCircle2 className={icon} /> Commissioned
      </button>
    );
  }
  if (stage === "expired_request") {
    return (
      <button type="button" onClick={onOpenTab} aria-pressed={active} className={statusCls(active, "amber", variant)} data-testid="commission-cta-expired">
        <Clock className={icon} /> Costs being checked
      </button>
    );
  }
  if (stage === "signing" || stage === "client_paper_submitting") {
    return (
      <button type="button" onClick={onOpenTab} aria-pressed={active} className={statusCls(active, "sky", variant)} data-testid="commission-cta-busy">
        <Loader2 className={`${icon} animate-spin`} /> Processing
      </button>
    );
  }

  const confirmFirst = stage === "open" && state.can_write;
  const click = () => {
    if (confirmFirst) {
      setError(null);
      setOpen(true);
    } else {
      onOpenTab();
    }
  };
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await commission.start();
      setOpen(false);
      onOpenTab();
    } catch (e) {
      setError(errorMessage(e, "We could not record your request. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const label = CONTINUE[stage] || (hero ? "Commission this project" : "Commission project");
  const cls = hero
    ? `${HERO} border ${active ? "border-white bg-white text-[#0B1220] shadow-lg" : "border-[#3EA8DC] bg-[#3EA8DC] hover:bg-[#5BB8E4] hover:border-[#5BB8E4] text-[#0B1220] shadow-[0_12px_30px_-10px_rgba(62,168,220,0.8)] hover:shadow-[0_14px_34px_-10px_rgba(62,168,220,0.9)]"}`
    : `${PILL} border ${active ? "border-[#1B4F8A] bg-white text-[#1B4F8A] shadow-sm" : "border-[#1B4F8A] bg-[#1B4F8A] hover:bg-[#143E72] hover:border-[#143E72] text-white shadow-sm hover:shadow-md"}`;
  return (
    <>
      <button type="button" onClick={click} aria-pressed={active} data-testid="commission-cta-button" title={stage === "open" ? "Takes about ten minutes" : undefined} className={cls}>
        <FileSignature className={icon} aria-hidden />
        {label}
        {hero && <ArrowRight className="w-4 h-4" aria-hidden />}
      </button>
      {open && <ConfirmDialog state={state} d={d} onClose={() => setOpen(false)} onConfirm={confirm} busy={busy} error={error} />}
    </>
  );
}
